import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import {
  SOLAR_PRODUCT_OFFERING_SUPERSEDED_CODE,
  solarProposalErrorCode,
} from "~/api/solar-proposal-errors"

import { refetchAfterProductOfferingSave } from "./solar-price-dependents"
import type {
  SolarProposalProductOfferingDto,
  UpdateSolarProductOfferingDto,
} from "../dto/solar-proposal-product-offering.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type UpdateSolarProductOfferingVariables = {
  proposalId: string
  body: UpdateSolarProductOfferingDto
}

/**
 * Saves Cash, its Finance Program and the design equipment. The answer is the
 * product-offering read itself; a failure refetches it, since a lock or a
 * partner change may be why it failed. The seller's draft is local and
 * survives either way.
 */
export default function useUpdateSolarProductOffering(): UseMutationResult<
  SolarProposalProductOfferingDto,
  unknown,
  UpdateSolarProductOfferingVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }: UpdateSolarProductOfferingVariables) =>
      solarProposalService.putProductOffering(proposalId, body),
    onSuccess: (saved, { proposalId }) =>
      refetchAfterProductOfferingSave(queryClient, proposalId, saved),
    onError: (error, { proposalId }) => {
      // Superseded means the options were for an older proposal, Site or
      // design, so the cached previews go too, not just the saved read.
      const superseded =
        solarProposalErrorCode(error) === SOLAR_PRODUCT_OFFERING_SUPERSEDED_CODE
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.productOffering(proposalId),
        ...(superseded ? {} : { exact: true }),
      })
    },
  })
}
